import CreditMeterButton from 'pages/credit-plans/CreditMeterButton';
import React, { useContext, useEffect, useState, useCallback } from 'react';
import {
  Switch,
  Route,
  Redirect,
  Link,
  useParams,
  useHistory,
} from 'react-router-dom';
import { GlobalContext } from '../../providers/GlobalProvider';
import RequiredLicense from 'pages/assign/RequiredLicense';
import { LicenseType } from 'pages/assign/LicenseBadge';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import { Tooltip } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import {
  ExportHubAPI,
  ExportDatabase,
  ExportProduct,
  WorkspaceFeedItem,
} from 'api/ExportHubAPI';
import DatabasesPanel from 'components/ExportHub/DatabasesPanel';
import CreateDatabaseModal from 'components/ExportHub/CreateDatabaseModal';
import ProductsPanel from 'components/ExportHub/ProductsPanel';
import AddProductModal from 'components/ExportHub/AddProductModal';
import ImportProductsModal from 'components/ExportHub/ImportProductsModal';
import ProductDetail from 'components/ExportHub/ProductDetail';
import LicensesPanel from 'components/ExportHub/LicensesPanel';
import ProductsOverview from 'components/ExportHub/ProductsOverview';
import LicenseOverview from 'components/ExportHub/LicenseOverview';
import DatabaseDashboard from 'components/ExportHub/DatabaseDashboard';
import DatabaseHistory from 'components/ExportHub/DatabaseHistory';
import DatabaseScreeningSettings from 'components/ExportHub/DatabaseScreeningSettings';
import GenerateReportModal from 'components/ExportHub/GenerateReportModal';
import IntegrationsTab from 'components/ExportHub/IntegrationsTab';
import RegulationsTab from 'components/ExportHub/RegulationsTab';
import NotificationsTab from 'components/ExportHub/NotificationsTab';
import Product360 from 'components/ExportHub/Product360';
import GlobalProductSearch from 'components/ExportHub/GlobalProductSearch';
import './export-hub.css';

/**
 * Export Hub shell. Owns the tool's page-level chrome (header, breadcrumb,
 * underlined tabs) and the internal /export-hub/* route skeleton. Renders inside
 * the app's global Header + NewSidebar — no second header/sidebar (spec §3.1).
 *
 * Slice 2 makes the Databases tab real (list + Create modal + enter-database);
 * the other Level 1 tabs and the Level 2 sections remain themed stubs until
 * their slices.
 *
 * Access is gated to the Business license tier (CLAUDE.md §4); the gate
 * self-hides for licensed users so it can sit unconditionally at the top.
 */

// Level 1 (workspace) tabs — see spec §3.4.
const L1_TABS: Array<{
  key: string;
  label: string;
  preview?: boolean;
  hidden?: boolean;
}> = [
  { key: 'databases', label: 'Databases' },
  { key: 'products', label: 'Products Overview' },
  { key: 'licenses', label: 'License Overview' },
  // Design-only placeholders. Kept wired but hidden so they don't read as
  // working features to customers — flip `hidden` to bring either one back.
  { key: 'integrations', label: 'Integrations', preview: true, hidden: true },
  { key: 'regulations', label: 'Regulations', preview: true, hidden: true },
  { key: 'notifications', label: 'Notifications' },
];
const VISIBLE_L1_TABS = L1_TABS.filter((t) => !t.hidden);
// Hidden tabs are not valid destinations either, so an old link to one lands
// on Databases rather than on a placeholder with no tab to return from.
const L1_KEYS = VISIBLE_L1_TABS.map((t) => t.key);

// Level 2 (inside a database) section tabs — see spec §3.4.
const L2_SECTIONS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'products', label: 'Products' },
  { key: 'licenses', label: 'Licenses' },
];
// Routable but not in the tab bar — reached from the header icons, the way the
// Entity Hub's database page does it. Still URL-addressable and shareable.
const L2_ICON_SECTIONS = ['settings', 'history'];
const L2_KEYS = [...L2_SECTIONS.map((s) => s.key), ...L2_ICON_SECTIONS];

const STUB_COPY: Record<string, { title: string; sub: string }> = {
  products: {
    title: 'Products Overview',
    sub: 'The cross-database product table and faceted filters arrive in a later slice.',
  },
  licenses: {
    title: 'License Overview',
    sub: 'Aggregated license analytics (geographic, ECCN, agency) arrive in a later slice.',
  },
  integrations: {
    title: 'Integrations',
    sub: 'Design-only ecosystem surfaces (ERP, API, SFTP, export-management) — coming soon.',
  },
  regulations: {
    title: 'Regulations',
    sub: 'A proactive export-regulatory news feed — design-only, coming soon.',
  },
  notifications: {
    title: 'Notifications',
    sub: 'The workspace notification feed is wired up in the final slice.',
  },
};

const EmptyPanel: React.FC<{ stubKey: string }> = ({ stubKey }) => {
  const copy = STUB_COPY[stubKey] || {
    title: 'Coming soon',
    sub: 'This section is part of a later build slice.',
  };
  return (
    <div className='export-hub-panel'>
      <div className='export-hub-empty'>
        <Inventory2OutlinedIcon
          className='export-hub-empty-icon'
          style={{ fontSize: 32 }}
        />
        <div className='export-hub-empty-title'>{copy.title}</div>
        <div className='export-hub-empty-sub'>{copy.sub}</div>
      </div>
    </div>
  );
};

/** Level 1 workspace view: page header + tabs + active panel. */
const Level1View: React.FC = () => {
  const { tab } = useParams<{ tab: string }>();
  const { cur_user } = useContext(GlobalContext);
  const history = useHistory();
  const groupId = cur_user?.selected_team?.id;

  const [createOpen, setCreateOpen] = useState(false);

  // Notifications feed (spec §5.4) — fetched once for the unread badge; the
  // Notifications tab reuses it. "Seen" cutoff is tracked per team in localStorage.
  const seenKey = groupId ? `eh-notif-seen-${groupId}` : '';
  const [feed, setFeed] = useState<WorkspaceFeedItem[]>([]);
  const [feedLoading, setFeedLoading] = useState(true);
  const [lastSeen, setLastSeen] = useState<number>(() => {
    if (!seenKey) return 0;
    const v = Number(localStorage.getItem(seenKey));
    return Number.isFinite(v) ? v : 0;
  });

  useEffect(() => {
    if (!groupId) return undefined;
    const key = `eh-notif-seen-${groupId}`;
    const v = Number(localStorage.getItem(key));
    setLastSeen(Number.isFinite(v) ? v : 0);
    let active = true;
    setFeedLoading(true);
    ExportHubAPI.getNotifications(groupId, 60)
      .then((items) => {
        if (active) setFeed(items);
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setFeedLoading(false);
      });
    return () => {
      active = false;
    };
  }, [groupId]);

  const markSeen = useCallback(() => {
    if (!seenKey) return;
    const now = Date.now();
    localStorage.setItem(seenKey, String(now));
    setLastSeen(now);
  }, [seenKey]);

  // Hooks must run before any early return.
  const isKnownTab = L1_KEYS.includes(tab);

  const openDatabase = (db: ExportDatabase) => {
    history.push(`/export-hub/databases/${db.id}/dashboard`);
  };

  if (!isKnownTab) {
    return <Redirect to='/export-hub/databases' />;
  }

  const unreadCount = feed.filter(
    (f) => new Date(f.created_at).getTime() > lastSeen,
  ).length;

  return (
    <>
      {/* Main hub header — always present above the tabs (spec §3.1). Each tab
          renders its own section sub-header beneath the tabs. */}
      <div className='export-hub-header'>
        <div>
          <h1 className='export-hub-title'>Export Hub</h1>
          <p className='export-hub-subtitle'>
            Browse databases, monitor product compliance and licenses, and stay
            on top of notifications.
          </p>
        </div>
        <div className='export-hub-header-actions'>
          <GlobalProductSearch />
          <CreditMeterButton packType='database' size='sm' />
        </div>
      </div>

      <nav className='export-hub-tabs' aria-label='Export Hub sections'>
        {VISIBLE_L1_TABS.map((t) => (
          <Link
            key={t.key}
            to={`/export-hub/${t.key}`}
            className={`export-hub-tab ${t.key === tab ? 'active' : ''}`}
          >
            {t.label}
            {t.preview && (
              <span className='export-hub-preview-pill'>Preview</span>
            )}
            {t.key === 'notifications' && unreadCount > 0 && (
              <span className='export-hub-tab-badge'>{unreadCount}</span>
            )}
          </Link>
        ))}
      </nav>

      {tab === 'databases' ? (
        <DatabasesPanel
          groupId={groupId}
          onOpenDatabase={openDatabase}
          onRequestCreate={() => setCreateOpen(true)}
        />
      ) : tab === 'products' ? (
        <ProductsOverview />
      ) : tab === 'licenses' ? (
        <LicenseOverview />
      ) : tab === 'integrations' ? (
        <IntegrationsTab />
      ) : tab === 'regulations' ? (
        <RegulationsTab />
      ) : tab === 'notifications' ? (
        <NotificationsTab
          feed={feed}
          loading={feedLoading}
          lastSeen={lastSeen}
          onMarkSeen={markSeen}
        />
      ) : (
        <EmptyPanel stubKey={tab} />
      )}

      <CreateDatabaseModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(db) => {
          setCreateOpen(false);
          openDatabase(db);
        }}
        onCreatedForDocumentUpload={(db, file) => {
          setCreateOpen(false);
          history.push('/customs-entry-analyzer', {
            presetDatabaseId: db.id,
            presetDatabaseName: db.name,
            presetFile: file,
            returnTo: {
              label: db.name,
              path: `/export-hub/databases/${db.id}/dashboard`,
            },
          });
        }}
      />
    </>
  );
};

/** Level 2 view (inside one database): breadcrumb + section tabs + content. */
const Level2View: React.FC = () => {
  const { databaseId, section } = useParams<{
    databaseId: string;
    section: string;
  }>();
  const { cur_user } = useContext(GlobalContext);
  const history = useHistory();
  const groupId = cur_user?.selected_team?.id;

  const [database, setDatabase] = useState<ExportDatabase | null>(null);
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [productsReloadKey, setProductsReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    if (databaseId && groupId) {
      ExportHubAPI.getDatabase(databaseId, groupId)
        .then((db) => {
          if (active) setDatabase(db);
        })
        .catch((err) => {
          console.error('Failed to load database:', err);
        });
    }
    return () => {
      active = false;
    };
  }, [databaseId, groupId]);

  if (!L2_KEYS.includes(section)) {
    return <Redirect to={`/export-hub/databases/${databaseId}/dashboard`} />;
  }

  const dbName = database?.name || 'Database';

  const openProduct = (p: ExportProduct) => {
    history.push(`/export-hub/databases/${databaseId}/products/${p.id}`);
  };

  // Header icons toggle their section: clicking the active one returns to the
  // dashboard, so the icon behaves like a switch rather than a dead end.
  const goSection = (key: string): void => {
    const target = section === key ? 'dashboard' : key;
    history.push(`/export-hub/databases/${databaseId}/${target}`);
  };
  const iconColor = (key: string): string =>
    section === key ? 'var(--action-color)' : 'var(--card-text)';

  const handleDeleteDatabase = async (): Promise<void> => {
    if (!databaseId || !groupId) return;
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      `Delete "${dbName}"?\n\nThis permanently deletes the database and all of ` +
        `its products along with their screenings, classification results, and ` +
        `license links. This cannot be undone.`,
    );
    if (!confirmed) return;
    try {
      await ExportHubAPI.deleteDatabase(databaseId, groupId);
      enqueueSnackbar(`Deleted "${dbName}".`, { variant: 'success' });
      history.push('/export-hub/databases');
    } catch (err) {
      console.error('Failed to delete database:', err);
      enqueueSnackbar('Failed to delete database.', { variant: 'error' });
    }
  };

  return (
    <>
      {/* Breadcrumb (spec §3.3). */}
      <div className='export-hub-breadcrumb'>
        <Link to='/export-hub/databases' className='export-hub-crumb-link'>
          <HomeOutlinedIcon style={{ fontSize: 16, verticalAlign: 'text-bottom', marginRight: 4 }} />
          Export Hub
        </Link>
        <span className='export-hub-crumb-sep'>
          <ChevronRightIcon style={{ fontSize: 16 }} />
        </span>
        <span className='export-hub-crumb-current'>{dbName}</span>
      </div>

      <div className='export-hub-header'>
        <div>
          <h1 className='export-hub-title'>{dbName}</h1>
          <p className='export-hub-subtitle'>
            {database?.description ||
              'Database-scoped dashboard, products, and licenses.'}
          </p>
        </div>
        <div className='export-hub-header-actions'>
          <CreditMeterButton packType='database' size='sm' />
          <button
            className='btn-primary'
            type='button'
            onClick={() => setAddProductOpen(true)}
          >
            <AddIcon style={{ fontSize: 18 }} /> Add Product
          </button>
          <button
            className='btn-outline'
            type='button'
            onClick={() => setImportOpen(true)}
          >
            <FileUploadOutlinedIcon style={{ fontSize: 18 }} /> Import CSV
          </button>
          <button
            className='btn-outline eh-btn-danger'
            type='button'
            onClick={handleDeleteDatabase}
          >
            <DeleteOutlineIcon style={{ fontSize: 18 }} /> Delete Database
          </button>

          {/* Database-level tools. Settings/History are their own sections so
              they stay linkable; Reports is an action, so it opens a modal. */}
          <div className='eh-header-icons'>
            <Tooltip title='Database Settings' placement='bottom' arrow>
              <button
                type='button'
                className='audit-btn-star'
                aria-label='Database Settings'
                onClick={() => goSection('settings')}
              >
                <SettingsOutlinedIcon
                  sx={{ fontSize: 20, color: iconColor('settings') }}
                />
              </button>
            </Tooltip>
            <Tooltip title='Database History' placement='bottom' arrow>
              <button
                type='button'
                className='audit-btn-star'
                aria-label='Database History'
                onClick={() => goSection('history')}
              >
                <AccessTimeOutlinedIcon
                  sx={{ fontSize: 20, color: iconColor('history') }}
                />
              </button>
            </Tooltip>
            <Tooltip title='Generate Reports' placement='bottom' arrow>
              <button
                type='button'
                className='audit-btn-star'
                aria-label='Generate Reports'
                onClick={() => setReportOpen(true)}
              >
                <SummarizeOutlinedIcon
                  sx={{ fontSize: 20, color: 'var(--card-text)' }}
                />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      <nav className='export-hub-tabs' aria-label='Database sections'>
        {L2_SECTIONS.map((s) => (
          <Link
            key={s.key}
            to={`/export-hub/databases/${databaseId}/${s.key}`}
            className={`export-hub-tab ${s.key === section ? 'active' : ''}`}
          >
            {s.label}
          </Link>
        ))}
      </nav>

      {section === 'products' ? (
        <ProductsPanel
          databaseId={databaseId}
          groupId={groupId}
          onOpenProduct={openProduct}
          onRequestAdd={() => setAddProductOpen(true)}
          onRequestImport={() => setImportOpen(true)}
          reloadKey={productsReloadKey}
        />
      ) : section === 'licenses' ? (
        <LicensesPanel databaseId={databaseId} groupId={groupId} />
      ) : section === 'settings' ? (
        <DatabaseScreeningSettings databaseId={databaseId} />
      ) : section === 'history' ? (
        <DatabaseHistory databaseId={databaseId} />
      ) : (
        <DatabaseDashboard databaseId={databaseId} />
      )}

      <AddProductModal
        open={addProductOpen}
        databaseId={databaseId}
        onClose={() => setAddProductOpen(false)}
        onCreated={() => {
          setAddProductOpen(false);
          setProductsReloadKey((k) => k + 1);
          // Make sure the user sees the new row on the products tab.
          if (section !== 'products') {
            history.push(`/export-hub/databases/${databaseId}/products`);
          }
        }}
      />

      <GenerateReportModal
        open={reportOpen}
        databaseId={databaseId}
        databaseName={dbName}
        onClose={() => setReportOpen(false)}
      />

      <ImportProductsModal
        open={importOpen}
        databaseId={databaseId}
        onClose={() => setImportOpen(false)}
        onImported={() => {
          setProductsReloadKey((k) => k + 1);
          if (section !== 'products') {
            history.push(`/export-hub/databases/${databaseId}/products`);
          }
        }}
      />
    </>
  );
};

const ExportHub: React.FC = () => {
  const { cur_user, isWhiteLabel } = useContext(GlobalContext);

  const normalizedLicense = (
    ['none', 'standard', 'pro', 'business', 'enterprise'].includes(
      cur_user?.license,
    )
      ? cur_user.license
      : 'none'
  ) as LicenseType;

  return (
    <section className='export-hub-root'>
      {!isWhiteLabel && (
        <RequiredLicense
          userLicense={normalizedLicense}
          minimumLicense='business'
        />
      )}

      <div className='export-hub-body'>
        <Switch>
        <Route
          exact
          path='/export-hub'
          render={() => <Redirect to='/export-hub/databases' />}
        />
        {/* Legacy "workspace dashboard" route → License Overview (spec §3.6). */}
        <Route
          exact
          path='/export-hub/dashboard'
          render={() => <Redirect to='/export-hub/licenses' />}
        />
        {/* Level 2 — inside a database. Specific (deeper) before general. */}
        <Route
          exact
          path='/export-hub/databases/:databaseId/products/:productId'
          component={ProductDetail}
        />
        <Route
          exact
          path='/export-hub/databases/:databaseId/:section'
          component={Level2View}
        />
        <Route
          exact
          path='/export-hub/databases/:databaseId'
          render={({ match }) => (
            <Redirect
              to={`/export-hub/databases/${match.params.databaseId}/dashboard`}
            />
          )}
        />
        {/* Product 360 / history. */}
        <Route
          exact
          path='/export-hub/products/:productId/history'
          component={Product360}
        />
        {/* Level 1 — single-segment workspace tabs. */}
        <Route exact path='/export-hub/:tab' component={Level1View} />
        <Redirect to='/export-hub/databases' />
        </Switch>
      </div>
    </section>
  );
};

export default ExportHub;
